import React, { useState, useEffect } from 'react';
import { useUnifiedAuth } from '../hooks/useUnifiedAuth';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  isPushNotificationSupported,
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isPushNotificationSubscribed
} from '../utils/pushNotifications';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NotificationBell = () => {
  const { user, authToken } = useUnifiedAuth();
  const { t, isRTL } = useLanguage();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [recentNotifications, setRecentNotifications] = useState([]);
  const [isPushEnabled, setIsPushEnabled] = useState(false);
  const [pushSupported, setPushSupported] = useState(false);

  useEffect(() => {
    // Check if push notifications are supported
    setPushSupported(isPushNotificationSupported());
    
    if (user) {
      fetchNotifications();
      checkPushSubscription();
      
      // Poll for new notifications every 30 seconds
      const interval = setInterval(fetchNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const checkPushSubscription = async () => {
    try {
      const subscribed = await isPushNotificationSubscribed();
      setIsPushEnabled(subscribed);
    } catch (error) {
      console.error('Error checking push subscription:', error);
    }
  };

  const togglePushNotifications = async () => {
    try {
      if (isPushEnabled) {
        // Unsubscribe
        await unsubscribeFromPushNotifications(authToken);
        setIsPushEnabled(false);
        alert('✅ Push notifications disabled successfully');
      } else {
        // Subscribe
        console.log('🔔 Attempting to subscribe to push notifications...');
        console.log('Auth token available:', !!authToken);
        
        await subscribeToPushNotifications(authToken);
        setIsPushEnabled(true);
        alert('✅ Push notifications enabled! You will now receive notifications even when the app is closed.');
      }
    } catch (error) {
      console.error('❌ Error toggling push notifications:', error);
      
      // Provide more detailed error message
      let errorMessage = 'Failed to update push notification settings. ';
      
      if (error.message) {
        if (error.message.includes('permission')) {
          errorMessage += 'Please allow notifications in your browser settings.';
        } else if (error.message.includes('Service Worker')) {
          errorMessage += 'Service worker registration failed. Please refresh the page.';
        } else if (error.message.includes('denied')) {
          errorMessage += 'Notification permission was denied.';
        } else {
          errorMessage += error.message;
        }
      } else if (error.response) {
        errorMessage += `Server error: ${error.response.status} - ${error.response.data?.detail || error.response.statusText}`;
      } else {
        errorMessage += 'Please check your browser console for details.';
      }
      
      alert(errorMessage);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      const notifications = response.data;
      setUnreadCount(notifications.filter(n => !n.read).length);
      setRecentNotifications(notifications.slice(0, 5)); // Get 5 most recent
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      setUnreadCount(prev => Math.max(0, prev - 1));
      setRecentNotifications(prev => 
        prev.map(n => 
          n.id === notificationId ? { ...n, read: true } : n
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment_reminder':
      case 'appointment_reminder_24h':
      case 'appointment_reminder_1h':
        return '⏰';
      case 'appointment_confirmed':
      case 'appointment_booked':
        return '✅';
      case 'appointment_rescheduled':
      case 'appointment_reschedule':
        return '🔄';
      case 'appointment_cancelled':
      case 'appointment_cancellation':
        return '❌';
      case 'message':
        return '💬';
      case 'system':
        return '🔔';
      default:
        return '📢';
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    
    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else {
      const diffInHours = Math.floor(diffInMinutes / 60);
      if (diffInHours < 24) {
        return `${diffInHours}h ago`;
      } else {
        const diffInDays = Math.floor(diffInHours / 24);
        return `${diffInDays}d ago`;
      }
    }
  };

  if (!user) return null;

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors"
      >
        <svg 
          className="w-6 h-6" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={2} 
            d="M15 17h5l-3.89 3.89a.996.996 0 01-1.78 0L15 17zM8 21a2 2 0 104 0M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9z" 
          />
        </svg>
        {unreadCount > 0 && (
          <span className="numeric-ltr absolute -top-1 -end-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {showDropdown && (
        <div className={`
          fixed md:absolute 
          inset-0 md:inset-auto 
          ${isRTL ? 'md:left-0' : 'md:right-0'} 
          md:mt-2 md:w-96 
          bg-white 
          md:rounded-lg md:shadow-lg md:border md:border-gray-200 
          z-50
          flex flex-col
          max-h-screen md:max-h-[600px]
        `}>
          {/* Mobile Header with Close Button */}
          <div className="p-4 md:p-4 border-b border-gray-200 flex-shrink-0">
            <div className={`flex items-center justify-between`}>
              <h3 className="font-semibold text-gray-900 text-lg md:text-base">{t('notifications')}</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setShowDropdown(false);
                    navigate('/notifications');
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium hidden md:block"
                >
                  View All
                </button>
                {/* Close button for mobile */}
                <button
                  onClick={() => setShowDropdown(false)}
                  className="md:hidden p-2 hover:bg-gray-100 rounded-full"
                  aria-label="Close notifications"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Push Notification Toggle */}
            {pushSupported && (
              <div className="mt-3 flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">🔔 Push Notifications</span>
                </div>
                <button
                  onClick={togglePushNotifications}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    isPushEnabled ? 'bg-blue-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      isPushEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )}
          </div>

          {/* Scrollable notifications list */}
          <div className="flex-1 overflow-y-auto">
            {recentNotifications.length === 0 ? (
              <div className="p-8 md:p-6 text-center">
                <div className="text-5xl md:text-4xl mb-3">🔔</div>
                <p className="text-gray-500 text-base md:text-sm">{t('noNotifications')}</p>
              </div>
            ) : (
              recentNotifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-5 md:p-4 hover:bg-gray-50 active:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    !notification.read ? 'bg-blue-50' : ''
                  }`}
                  onClick={() => {
                    if (!notification.read) {
                      markAsRead(notification.id);
                    }
                    setShowDropdown(false);
                    if (notification.type === 'message' && notification.data?.conversation_id) {
                      navigate('/messages');
                    } else if (notification.type.includes('appointment') && notification.data?.appointment_id) {
                      navigate('/appointments');
                    }
                  }}
                >
                  <div className={`flex items-start gap-3 md:gap-3`}>
                    <div className="text-2xl md:text-lg flex-shrink-0">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-gray-900 text-base md:text-sm leading-snug">
                        {notification.title}
                      </h4>
                      <p className="text-gray-600 text-sm md:text-sm mt-1.5 md:mt-1 line-clamp-2 leading-relaxed">
                        {notification.message}
                      </p>
                      <div className={`flex items-center justify-between mt-2.5 md:mt-2`}>
                        <span className="text-xs text-gray-500">
                          {formatTimeAgo(notification.created_at)}
                        </span>
                        {!notification.read && (
                          <span className="w-2.5 h-2.5 md:w-2 md:h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer - View All button */}
          {recentNotifications.length > 0 && (
            <div className="p-4 md:p-3 border-t border-gray-200 flex-shrink-0 md:hidden">
              <button
                onClick={() => {
                  setShowDropdown(false);
                  navigate('/notifications');
                }}
                className="w-full py-3 text-center text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-base font-medium transition-colors"
              >
                {t('viewAllNotifications', 'View All Notifications')}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Overlay to close dropdown - only on desktop */}
      {showDropdown && (
        <div
          className="hidden md:block fixed inset-0 z-40"
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

export default NotificationBell;